// eslint-disable-next-line import/no-unassigned-import
import 'froala-editor/js/plugins.pkgd.min.js';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { AngularSplitModule } from 'angular-split';

import { KitchenAngularModule } from '@inmoment/the-kitchen-angular';

import { AppService } from './app.service';
import { ConfigurationPillComponent } from './components/configuration-pill/configuration-pill.component';
import { EmptySidePanelComponent } from './components/empty-side-panel/empty-side-panel.component';
import { EditModeDirective } from './components/inline-edit/edit-mode.directive';
import { EditOnEnterDirective } from './components/inline-edit/edit-on-enter.directive';
import { InlineEditComponent } from './components/inline-edit/inline-edit.component';
import { ViewModeDirective } from './components/inline-edit/view-mode.directive';
import { SimpleDialogComponent } from './components/simple-dialog.component';
import { LexAutocompleteComponent } from './components/uikit/lex-autocomplete/lex-autocomplete.component';
import { LexBluePillComponent } from './components/uikit/lex-blue-pill/lex-blue-pill.component';
import { LexButtonComponent } from './components/uikit/lex-button/lex-button.component';
import { LexChangeIndicatorComponent } from './components/uikit/lex-change-indicator/lex-change-indicator.component';
import { LexHorizontalStepperComponent } from './components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';
import { LexIconButtonComponent } from './components/uikit/lex-icon-button/lex-icon-button.component';
import { LexInputComponent } from './components/uikit/lex-input/lex-input.component';
import { LexNumericInputComponent } from './components/uikit/lex-numeric-input/lex-numeric-input.component';
import { LexProgressBarComponent } from './components/uikit/lex-progress-bar/lex-progress-bar.component';
import { LexSelectComponent } from './components/uikit/lex-select/lex-select.component';
import { LexTableCellDirective } from './components/uikit/lex-table/lex-table-cell.directive';
import { LexTableComponent } from './components/uikit/lex-table/lex-table.component';
import { LexTagButtonComponent } from './components/uikit/lex-tag-button/lex-tag-button.component';
import { LexXiActionButtonBarComponent } from './components/xi-components/lex-xi-action-button-bar/lex-xi-action-button-bar.component';
import { LexXiModalComponent } from './components/xi-components/lex-xi-modal/lex-xi-modal.component';
import { LexXiPopoutComponent } from './components/xi-components/lex-xi-popout/lex-xi-popout.component';
import { LexXiSidePanelComponent } from './components/xi-components/lex-xi-side-panel/lex-xi-side-panel.component';
import { LexXiSimpleModalComponent } from './components/xi-components/lex-xi-simple-modal/lex-xi-simple-modal.component';
import { LexXiSnackBarComponent } from './components/xi-components/lex-xi-snack-bar/lex-xi-snack-bar.component';
import { LexXiTagComponent } from './components/xi-components/lex-xi-tag/lex-xi-tag.component';
import { LexXiWarningPopupComponent } from './components/xi-components/lex-xi-warning-popup/lex-xi-warning-popup.component';
import { AutofocusDirective } from './directives/auto-focus.directive';
import { AutocompleteOffDirective } from './directives/autocomplete-off.directive';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { EditableDirective } from './directives/contenteditable.directive';
import { DropdownTriggerForDirective } from './directives/dropdown-trigger-for.directive';
import { MiddleEllipsizeFragmentComponent } from './directives/middle-ellipsize-fragment.component';
import { MiddleEllipsizeDirective } from './directives/middle-ellipsize.directive';
import { NumericInputDirective } from './directives/numeric-input.directive';
import { OnlyDigitDirective } from './directives/only-digit.directive';
import { ShowMatTooltipIfDisabledDirective } from './directives/show-mat-tooltip-if-disabled.directive';
import { ShowMatTooltipIfTruncatedDirective } from './directives/show-mattooltip-if-truncated.directive';
import { XiModalContentDirective } from './directives/xi-modal-content.directive';
import { XiPopoutContentDirective } from './directives/xi-popout-content.directive';
import { AccountChangeTimezoneComponent } from './modules/account/components/account-change-timezone/account-change-timezone.component';
import { ChangeLanguageComponent } from './modules/account/components/change-language/change-language.component';
import { DocumentNlpOutputComponent } from './modules/collection/components/document/document-nlp-output/document-nlp-output.component';
import { SentimentDisplayComponent } from './modules/collection/components/sentiment-display/sentiment-display.component';
import { CloneConfigurationDialogComponent } from './modules/configuration/components/clone-configuration-dialog/clone-configuration-dialog.component';
import { MoveConfigurationDialogComponent } from './modules/configuration/components/move-configuration-dialog/move-configuration-dialog.component';
import { TagAutowrapComponent } from './modules/configuration/components/tag-autowrap/tag-autowrap.component';
import { AnalysisConfigSelectorComponent } from './modules/project/components/analysis-config-selector/analysis-config-selector.component';
import { AnalysisDetailsComponent } from './modules/project/components/analysis-details/analysis-details.component';
import { AnalysisNameStepComponent } from './modules/project/components/driver-analysis-dialog/analysis-name-step/analysis-name-step.component';
import { AnalysisTargetStepComponent } from './modules/project/components/driver-analysis-dialog/analysis-target-step/analysis-target-step.component';
import { DriverAnalysisDialogComponent } from './modules/project/components/driver-analysis-dialog/driver-analysis-dialog.component';
import { DriverQuestionsStepComponent } from './modules/project/components/driver-analysis-dialog/driver-questions-step/driver-questions-step.component';
import { HierarchyStepComponent } from './modules/project/components/driver-analysis-dialog/hierarchy-step/hierarchy-step.component';
import { WeightStepComponent } from './modules/project/components/driver-analysis-dialog/weight-step/weight-step.component';
import { NewAnalysisComponent } from './modules/project/components/new-analysis/new-analysis.component';
import { NewDashboardComponent } from './modules/project/components/new-dashboard/new-dashboard.component';
import { RenameCollectionComponent } from './modules/project/components/rename-collection/rename-collection.component';
import { SelectCollectionConfigurationDialogComponent } from './modules/project/components/select-collection-configuration-dialog/select-collection-configuration-dialog.component';
import { UploadInfoDialogComponent } from './modules/upload/components/upload-info/upload-info.component';
import { DataSourceChooserComponent } from './modules/widget/components/data-source-chooser/data-source-chooser.component';
import { IsValidNumberPipe } from './modules/widget/components/widget-big-number/is-valid-number.pipe';
import {
  AreaTooltipPipe,
  BytesPipe,
  CellCommentPipe,
  GetAreaCommentsPipe,
  GridColumnPartPipe,
  HumanizeNumberPipe,
  ImpactTooltipPipe,
  IsAnalysisDisabledPipe,
  LexDatePipe,
  PinnedCommentPipe,
  SideloadedLabelPipe,
  TruncateEndPipe,
  TruncatePipe,
  RoundHalfAwayPipe,
  ConversionPipe,
  TimeIntervalPipe,
} from './pipes';
import { AIAgentAPIService } from './providers/ai-agent-api.service';
import { SemantriaAccount } from './providers/bridge-api/account.service';
import { AliasService } from './providers/bridge-api/alias.service';
import {
  AccountFeatureGuardService,
  AdminAuthGuardService,
  AuthGuardService,
  NotExecutiveGuardService,
} from './providers/bridge-api/auth.service';
import { DashboardService } from './providers/bridge-api/dashboard.service';
import { SemantriaPreferences } from './providers/bridge-api/preferences.service';
import { SavedViewService } from './providers/bridge-api/saved-view.service';
import { SocialLoginService } from './providers/bridge-api/social-login.service';
import { SemantriaUsersService } from './providers/semantria-api/semantria-users.service';
import { ActionPlanService } from './providers/storage-api/actionplan.service';
import { AnalysisGroupService } from './providers/storage-api/analysis-group-service';
import { AnalysisService } from './providers/storage-api/analysis.service';
import { AnnotationService } from './providers/storage-api/annotation.service';
import { AuditService } from './providers/storage-api/audit.service';
import { StorageConfigurationService } from './providers/storage-api/blender-configuration.service';
import { CollectionService } from './providers/storage-api/collection.service';
import { DocumentService } from './providers/storage-api/document.service';
import { InteractiveSmartSummaryService } from './providers/storage-api/interactive-smart-summary.service';
import { LookupService } from './providers/storage-api/lookup.service';
import { PredictiveService } from './providers/storage-api/predictive.service';
import { ProjectService } from './providers/storage-api/project.service';
import { ReportService } from './providers/storage-api/report.service';
import { UploadService } from './providers/storage-api/upload.service';
import { DownloadTasksService } from './providers/tasks-api/download.service';
import { AIToolService } from './services/aitool.service';
import { AlertService } from './services/alert.service';
import { ContextBridgeService } from './services/context-bridge.service';
import { MatPaginatorIntlCust } from './services/custom-paginator';
import { DialogService } from './services/dialog.service';
import { GroupRouteGuardService } from './services/group-route-guard.service';
import { IconService } from './services/icon.service';
import { LoggerService } from './services/logger.service';
import { SystemService } from './services/system.service';
import { TimezoneService } from './services/timezone.service';
import { WidgetLimiterService } from './services/widget-limiter.service';
import { TranslocoRootModule } from './transloco-root.module';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    MatListModule,
    MatButtonModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatTreeModule,
    MatChipsModule,
    MatBadgeModule,
    NgbPopoverModule,
    AngularSplitModule,
    OverlayModule,
    DragDropModule,
    KitchenAngularModule,
    TranslocoRootModule,
    FroalaEditorModule,
    FroalaViewModule,
    IsValidNumberPipe,
  ],
  providers: [
    {
      provide: ErrorStateMatcher,
      useClass: ShowOnDirtyErrorStateMatcher,
    },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlCust },
    AuthGuardService,
    AdminAuthGuardService,
    NotExecutiveGuardService,
    GroupRouteGuardService,
    AccountFeatureGuardService,
    AppService,
    AlertService,
    WidgetLimiterService,
    LoggerService,
    SocialLoginService,
    DialogService,
    SystemService,
    IconService,
    HumanizeNumberPipe,
    TruncatePipe,
    BytesPipe,
    IsAnalysisDisabledPipe,
    TruncateEndPipe,
    SemantriaAccount,
    SemantriaPreferences,
    TimezoneService,
    DashboardService,
    SavedViewService,
    AliasService,
    ProjectService,
    CollectionService,
    AnalysisService,
    AnalysisGroupService,
    DocumentService,
    ReportService,
    UploadService,
    AnnotationService,
    SemantriaUsersService,
    StorageConfigurationService,
    DownloadTasksService,
    PredictiveService,
    ActionPlanService,
    LookupService,
    InteractiveSmartSummaryService,
    AuditService,
    AIToolService,
    AIAgentAPIService,
    ContextBridgeService,
    provideHttpClient(withInterceptorsFromDi()),
    GetAreaCommentsPipe,
    ImpactTooltipPipe,
    RoundHalfAwayPipe,
    ConversionPipe,
    GridColumnPartPipe,
    CellCommentPipe,
    PinnedCommentPipe,
    TimeIntervalPipe,
  ],
  declarations: [
    CloneConfigurationDialogComponent,
    SimpleDialogComponent,
    LexXiWarningPopupComponent,
    AutofocusDirective,
    AutocompleteOffDirective,
    OnlyDigitDirective,
    NumericInputDirective,
    MiddleEllipsizeFragmentComponent,
    MiddleEllipsizeDirective,
    HumanizeNumberPipe,
    TruncatePipe,
    BytesPipe,
    IsAnalysisDisabledPipe,
    TruncateEndPipe,
    LexDatePipe,
    DataSourceChooserComponent,
    SentimentDisplayComponent,
    LexButtonComponent,
    LexIconButtonComponent,
    LexTagButtonComponent,
    LexInputComponent,
    LexNumericInputComponent,
    LexSelectComponent,
    LexTableComponent,
    LexTableCellDirective,
    LexAutocompleteComponent,
    LexXiModalComponent,
    LexXiPopoutComponent,
    LexChangeIndicatorComponent,
    LexHorizontalStepperComponent,
    LexBluePillComponent,
    AnalysisConfigSelectorComponent,
    DropdownTriggerForDirective,
    XiModalContentDirective,
    XiPopoutContentDirective,
    ConfigurationPillComponent,
    LexXiSidePanelComponent,
    LexXiTagComponent,
    LexXiActionButtonBarComponent,
    DocumentNlpOutputComponent,
    LexXiSnackBarComponent,
    AccountChangeTimezoneComponent,
    MoveConfigurationDialogComponent,
    LexXiSimpleModalComponent,
    MoveConfigurationDialogComponent,
    TagAutowrapComponent,
    InlineEditComponent,
    ViewModeDirective,
    EditModeDirective,
    EditOnEnterDirective,
    EditableDirective,
    ShowMatTooltipIfDisabledDirective,
    ShowMatTooltipIfTruncatedDirective,
    LexProgressBarComponent,
    AnalysisDetailsComponent,
    DriverAnalysisDialogComponent,
    AnalysisNameStepComponent,
    DriverQuestionsStepComponent,
    HierarchyStepComponent,
    AnalysisTargetStepComponent,
    WeightStepComponent,
    ChangeLanguageComponent,
    RenameCollectionComponent,
    NewDashboardComponent,
    UploadInfoDialogComponent,
    NewAnalysisComponent,
    SelectCollectionConfigurationDialogComponent,
    EmptySidePanelComponent,
    ClickStopPropagationDirective,
    AreaTooltipPipe,
    SideloadedLabelPipe,
    GetAreaCommentsPipe,
    ImpactTooltipPipe,
    RoundHalfAwayPipe,
    ConversionPipe,
    GridColumnPartPipe,
    CellCommentPipe,
    PinnedCommentPipe,
    TimeIntervalPipe,
  ],
  exports: [
    CloneConfigurationDialogComponent,
    MoveConfigurationDialogComponent,
    CommonModule,
    FormsModule,
    HumanizeNumberPipe,
    TruncatePipe,
    BytesPipe,
    IsAnalysisDisabledPipe,
    TruncateEndPipe,
    LexDatePipe,
    CdkAccordionModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    MatListModule,
    MatButtonModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatTreeModule,
    MatChipsModule,
    MatBadgeModule,
    SimpleDialogComponent,
    LexXiWarningPopupComponent,
    AutofocusDirective,
    AutocompleteOffDirective,
    OnlyDigitDirective,
    NumericInputDirective,
    MiddleEllipsizeDirective,
    DropdownTriggerForDirective,
    XiModalContentDirective,
    XiPopoutContentDirective,
    DataSourceChooserComponent,
    SentimentDisplayComponent,
    AnalysisConfigSelectorComponent,
    LexButtonComponent,
    LexIconButtonComponent,
    LexTagButtonComponent,
    LexInputComponent,
    LexNumericInputComponent,
    LexSelectComponent,
    LexTableComponent,
    LexTableCellDirective,
    LexAutocompleteComponent,
    LexXiModalComponent,
    LexXiPopoutComponent,
    LexHorizontalStepperComponent,
    LexBluePillComponent,
    LexChangeIndicatorComponent,
    OverlayModule,
    DragDropModule,
    ConfigurationPillComponent,
    LexXiSidePanelComponent,
    LexXiTagComponent,
    LexXiActionButtonBarComponent,
    KitchenAngularModule,
    DocumentNlpOutputComponent,
    LexXiSnackBarComponent,
    AccountChangeTimezoneComponent,
    LexXiSimpleModalComponent,
    TagAutowrapComponent,
    InlineEditComponent,
    ViewModeDirective,
    EditModeDirective,
    EditOnEnterDirective,
    EditableDirective,
    TranslocoRootModule,
    ShowMatTooltipIfDisabledDirective,
    ShowMatTooltipIfTruncatedDirective,
    LexProgressBarComponent,
    AnalysisDetailsComponent,
    DriverAnalysisDialogComponent,
    AnalysisNameStepComponent,
    DriverQuestionsStepComponent,
    HierarchyStepComponent,
    AnalysisTargetStepComponent,
    WeightStepComponent,
    ChangeLanguageComponent,
    RenameCollectionComponent,
    NewDashboardComponent,
    UploadInfoDialogComponent,
    FroalaEditorModule,
    FroalaViewModule,
    NewAnalysisComponent,
    SelectCollectionConfigurationDialogComponent,
    EmptySidePanelComponent,
    ClickStopPropagationDirective,
    AreaTooltipPipe,
    SideloadedLabelPipe,
    GetAreaCommentsPipe,
    ImpactTooltipPipe,
    RoundHalfAwayPipe,
    ConversionPipe,
    GridColumnPartPipe,
    CellCommentPipe,
    PinnedCommentPipe,
    TimeIntervalPipe,
    IsValidNumberPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppSharedModule {
  constructor() {}
}
